import type {
  ProfitSharingConfig,
  RefundFeeProcessingMethod,
  BillingCycle,
  DefaultConfigType,
  BankDiscountConfigType,
  PbaReferralConfigType,
} from '../models/parking-contract.ts';
import type { GasStationAccountOwnerType } from '../models/refueling-contract.ts';
import type { ProfitSharingMethod as ChainStoreContractProfitSharingMethod } from '../models/chain-store-contract.ts';
import type {
  ProfileType,
  DisbursementFeeProcessingMethod,
  ReceiptMode,
} from '../models/contract.ts';

export function labelMapToOptionList<T extends Record<string, string>>(obj: T) {
  return Object.entries(obj).map(([value, label]) => ({
    label: label as keyof T,
    value: value as T[keyof T],
  }));
}

/* ===== Contract ===== */
export const PROFILE_TYPE_LABEL = {
  DEFAULT: '預設合約',
  SPECIFY: '指定場站合約',
} as Record<ProfileType, string>;

export const CONTRACT_TYPE_LABEL = {
  ParkingContract: '停車場',
  RefuelingContract: '加油站',
  ChainStoreContract: '連鎖店',
} as const;

export const RECEIPT_MODE_LABEL = {
  ONSITE_PARTNER: '現場開立業者發票',
  ONSITE_AUTOPASS: '現場開立 Autopass 發票',
  EZPAY_AUTOPASS: '開立 Autopass 電子發票',
  EXEMPTION: '法規許可免開立發票',
} as Record<ReceiptMode, string>;

/* ===== ChainStoreContract ===== */
export const CHAIN_STORE_CONTRACT_PROFIT_SHARING_TYPE_LABEL = {
  FIXED: '固定手續費',
  PERCENTAGE: '百分比',
} as Record<ChainStoreContractProfitSharingMethod, string>;

export const CHAIN_STORE_CONTRACT_PROFIT_SHARING_UNIT = {
  FIXED: '元',
  PERCENTAGE: '％',
} as Record<ChainStoreContractProfitSharingMethod, string>;

/* ===== RefuelingContract ===== */
export const REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL = {
  PRE_DEDUCTION: '加油費扣除手續費後匯出',
  NO_DEDUCTION: '加油費全額匯出',
} as Record<DisbursementFeeProcessingMethod, string>;

export const GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL = {
  JustLog: '不使用現銷金機制',
  Partner: '同一廠商共用',
  GasStation: '單一加油站獨立計算',
} as Record<GasStationAccountOwnerType, string>;

/* ===== ParkingContract ===== */
export const PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL = {
  PRE_DEDUCTION: '停車費扣除手續費後匯出',
  NO_DEDUCTION: '停車費全額匯出',
} as Record<DisbursementFeeProcessingMethod, string>;

export const PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL = {
  REFUND_ALL: '一律退回手續費',
  REFUND_CURRENT_PERIOD: '僅當期取消退回手續費',
  NO_REFUND: '不退回手續費',
} as Record<RefundFeeProcessingMethod, string>;

export const PARKING_CONTRACT_BILLING_CYCLE_LABEL = {
  MONTHLY: '每月',
  WEEKLY: '每週',
} as Record<BillingCycle, string>;

export const PARKING_CONTRACT_DEFAULT_PROFIT_SHARING_TYPE_LABEL = {
  FIXED_FEE: '固定手續費',
  FEE_PERCENTAGE: '依交易金額百分比',
} as Record<DefaultConfigType, string>;

export const PARKING_CONTRACT_BANK_DISCOUNT_PROFIT_SHARING_TYPE_LABEL = {
  NON: '無',
  HOURLY_PURCHASE_PRICE: '每小時變價撥款',
  PERCENTAGE: '依交易金額百分比',
} as Record<BankDiscountConfigType, string>;

export const PARKING_CONTRACT_PBA_REFERRAL_PROFIT_SHARING_TYPE_LABEL = {
  NON: '無',
  HOURLY_PURCHASE_PRICE: '每小時變價撥款',
  PERCENTAGE: '依交易金額百分比',
} as Record<PbaReferralConfigType, string>;

export const PARKING_CONTRACT_PROFIT_SHARING_UNIT = {
  FIXED_FEE: '元',
  FEE_PERCENTAGE: '％',
  HOURLY_PURCHASE_PRICE: '元',
  PERCENTAGE: '％',
} as Record<
  DefaultConfigType | BankDiscountConfigType | PbaReferralConfigType,
  string
>;

export const defaultProfitSharingText = (config: ProfitSharingConfig) => {
  switch (config.defaultConfig.type) {
    case 'FIXED_FEE':
      return `${PARKING_CONTRACT_DEFAULT_PROFIT_SHARING_TYPE_LABEL[config.defaultConfig.type]} ${config.defaultConfig.value} 元`;
    case 'FEE_PERCENTAGE':
      return `${PARKING_CONTRACT_DEFAULT_PROFIT_SHARING_TYPE_LABEL[config.defaultConfig.type]} ${config.defaultConfig.value} ％`;
    default:
      return '無';
  }
};

export const bankDiscountProfitSharingText = (config: ProfitSharingConfig) => {
  switch (config.bankDiscountConfig.type) {
    case 'HOURLY_PURCHASE_PRICE':
      return `${PARKING_CONTRACT_BANK_DISCOUNT_PROFIT_SHARING_TYPE_LABEL[config.bankDiscountConfig.type]} ${config.bankDiscountConfig.value} 元`;
    case 'PERCENTAGE':
      return `${PARKING_CONTRACT_BANK_DISCOUNT_PROFIT_SHARING_TYPE_LABEL[config.bankDiscountConfig.type]} ${config.bankDiscountConfig.value} ％`;
    default:
      return '無';
  }
};

export const pbaReferralProfitSharingText = (config: ProfitSharingConfig) => {
  switch (config.pbaReferralConfig.type) {
    case 'HOURLY_PURCHASE_PRICE':
      return `${PARKING_CONTRACT_PBA_REFERRAL_PROFIT_SHARING_TYPE_LABEL[config.pbaReferralConfig.type]} ${config.pbaReferralConfig.value} 元`;
    case 'PERCENTAGE':
      return `${PARKING_CONTRACT_PBA_REFERRAL_PROFIT_SHARING_TYPE_LABEL[config.pbaReferralConfig.type]} ${config.pbaReferralConfig.value} ％`;
    default:
      return '無';
  }
};

export const PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL = {
  ALPR: '車辨',
  KIOSK: '繳費機線上化',
  PAY_TAIPEI: '北市路外',
};
